import React from 'react';
import type { NativeAudienceMutationResult } from '@charitystack/schemas';
import { Badge } from '@/components/ui/badge';

// The result of a durable segment save, in plain language. Nothing here names
// the marketing vendor or shows its ids. The server's `reason` text is never
// rendered: it is stored with each save and older records carry vendor wording,
// so every state is described from its status instead.

export function NativeAudienceSaveOutcome({ result }: { result: NativeAudienceMutationResult }) {
  const saved = result.status === 'saved';
  const title = saved ? 'Saved' : result.status === 'conflict' ? 'Needs review' : 'Not confirmed';
  const description = saved
    ? result.presentation === 'last-observed-agrees'
      ? 'Your segment was saved, and our last check matches it.'
      : 'Your segment was saved. We’re still confirming it.'
    : result.status === 'conflict'
      ? 'This segment changed before or during the save. Reload and review it before making another change.'
      : 'We couldn’t confirm whether this save finished. Checking again uses the same request, so don’t start a new save.';

  return (
    <div
      className={saved
        ? 'rounded-md border border-status-green-text/30 bg-status-green-bg p-3 text-status-green-text'
        : 'rounded-md border border-status-amber-text/30 bg-status-amber-bg p-3 text-status-amber-text'}
      role={saved ? 'status' : 'alert'}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={saved ? 'success' : 'warning'}>{title}</Badge>
        {saved && result.presentation === 'saved-verification-pending' && <Badge variant="info">Still confirming</Badge>}
        {saved && result.presentation === 'last-observed-agrees' && <Badge variant="success">Last check matches</Badge>}
      </div>
      <p className="mt-2 text-sm font-medium">{description}</p>
      {result.acknowledgement && <p className="mt-1 text-xs">
        Saved {new Date(result.acknowledgement.acknowledgedAt).toLocaleString()}.
      </p>}
    </div>
  );
}
